import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router'
import { validate } from 'validate.js'
import useRouter from 'utils/useRouter'
import { useExameIdentificacaoNecropapiloscopicaService } from 'hooks/useExameIdentificacaoNecropapiloscopicaService'
import { useExameRealizadoService } from 'hooks/useExameRealizadoService'

const schemaExame = {
  tipoDocumento: {
    presence: { allowEmpty: false, message: '^O tipo do documento é obrigatório' },
  },
  nome: {
    presence: { allowEmpty: false, message: '^O nome é obrigatório' },
  },
  numeroDocumento: {
    presence: { allowEmpty: false, message: '^O número do documento é obrigatório' },
  },
}

export const useExameNecropapiloscopico = () => {
  const params = useParams()
  const router = useRouter()
  const exameNecropapiloscopicoService = useExameIdentificacaoNecropapiloscopicaService()
  const exameRealizadoService = useExameRealizadoService()

  const [activeStep, setActiveStep] = useState(0)
  const [exameNecropapiloscopico, setExameNecropapiloscopico] = useState(null)
  const [disableBotaoAvancar, setDisableBotaoAvancar] = useState(false)
  const [processando, setProcessando] = useState(false)
  const [processandoFinalizacaoExame, setProcessandoFinalizacaoExame] = useState(false)

  // Emulated Form State for validation compatibility
  const [formState, setFormState] = useState({
    isValid: false,
    values: {},
    touched: {},
    errors: {},
    isLoading: false,
  })

  // Validate formState values reactively
  useEffect(() => {
    const errors = validate(formState.values, schemaExame)
    setFormState((prevState) => ({
      ...prevState,
      isValid: !errors,
      errors: errors || {},
    }))
  }, [formState.values])

  const handleChange = useCallback((event) => {
    event.persist?.()
    const { name, value, type, checked } = event.target
    setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        [name]: type === 'checkbox' ? checked : value,
      },
      touched: {
        ...prevState.touched,
        [name]: true,
      },
    }))
  }, [])

  const handleChangeNewValueByNameProperty = useCallback((event, newValue, property) => {
    setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        [property]: newValue,
      },
      touched: {
        ...prevState.touched,
        [property]: true,
      },
    }))
  }, [])

  const hasError = useCallback((field) => {
    return !!(formState.touched[field] && formState.errors[field])
  }, [formState.touched, formState.errors])

  const form = {
    formState,
    setFormState,
    handleChange,
    handleChangeNewValueByNameProperty,
    hasError,
  }

  // Fetch exam data
  const fetchExameNecropapiloscopico = useCallback(async () => {
    try {
      const response = await exameNecropapiloscopicoService.listarExameNecropapiloscopicoDetalhadoAsync(
        params.id,
        setProcessando
      )
      setExameNecropapiloscopico(response)
    } catch (error) {
      console.error('Failed to fetch necropapiloscopic exam details', error)
    }
  }, [params.id])

  useEffect(() => {
    fetchExameNecropapiloscopico()
  }, [fetchExameNecropapiloscopico, activeStep])

  const handleVoltar = useCallback(() => {
    router.history.goBack()
  }, [router.history])

  const handleEtapaAnterior = useCallback(() => {
    if (activeStep === 0) return
    setActiveStep((prev) => prev - 1)
  }, [activeStep])

  const handleProximaEtapa = useCallback(async () => {
    if (activeStep === 0) {
      setActiveStep((prev) => prev + 1)
      return
    }

    if (activeStep === 1) {
      const filiacoes = []
      Object.keys(formState.values).forEach((key) => {
        if (key.indexOf('filiacao') !== -1) {
          filiacoes.push(formState.values[key])
        }
      })
      const dados = {
        ...formState.values,
        dataNascimento: formState.values.dataNascimentoFormatada,
        filiacoes,
      }

      try {
        await exameNecropapiloscopicoService.editarExameNecropapiloscopico(
          params.id,
          dados,
          setProcessandoFinalizacaoExame
        )
        await exameNecropapiloscopicoService.alterarDadosCadaverDeImpressaoAnalisada(params.id)
        await exameRealizadoService.gerarDocumento(params.id)

        router.history.push({
          pathname: `/casos/caso/solicitacao/exameSolicitado/editorExameRealizado/${params.id}`,
          state: { IgnoneUploadImagem: true },
        })
      } catch (error) {
        console.error('Failed to save necropapiloscopic exam details', error)
      }
    }
  }, [activeStep, formState.values, params.id, router.history])

  return {
    activeStep,
    exameNecropapiloscopico,
    disableBotaoAvancar,
    processando,
    processandoFinalizacaoExame,
    form,
    setDisableBotaoAvancar,
    handleVoltar,
    handleEtapaAnterior,
    handleProximaEtapa,
    idExameRealizado: params.id,
  }
}
